/**
 * SAFECARE Act - Bill Parameter Reference
 * Extracted from H.R. ____ (118th Congress, 1st Session)
 * 
 * This file contains every numerical parameter, rate, threshold, and formula
 * specified in the bill text, organized by title and section.
 * 
 * USE THIS AS THE SINGLE SOURCE OF TRUTH FOR MODEL PARAMETERS.
 */

export const SAFECARE_BILL_PARAMETERS = {

    // ========================================================================
    // TITLE II: FINANCING AND THE SAFECARE TRUST FUND
    // ========================================================================

    revenue: {
        // Sec 202(b): Employer Health Contribution
        employerRate: 0.08, // 8.0% of wages

        // Sec 203(c): Individual Health Contribution
        individualBaseRate: 0.035, // 3.5% of Modified AGI
        selfEmployedBaseRate: 0.115, // 11.5% of net earnings

        // Sec 203(c)(3): Additional Surtax on High Income
        surtaxBrackets: [
            { threshold: 100000, rate: 0.010 }, // +1.0% above $100k
            { threshold: 250000, rate: 0.015 }, // +1.5% above $250k (cumulative 2.5%)
            { threshold: 500000, rate: 0.020 }  // +2.0% above $500k (cumulative 4.5%)
        ],

        // Sec 203(b)(1)(B)(iv): Retirement Income Allowance
        retirementExemptionLimit: 100000, // $100,000 (indexed to CPI after 2026)
        // retirementExemptionPerFiler removed (not in statute)

        // Sec 205: Medicare HI Tax Replacement
        // Current HI tax: 1.45% employee + 1.45% employer = 2.9% total
        // SAFECARE replaces this (set to 0% when contributions take effect)
        replacedHITax: 0.029
    },

    costSharing: {
        // Sec 204(a): No deductibles or coinsurance
        deductibles: 0,
        coinsurance: 0,

        // Sec 204(d)(2): Annual Copay Cap
        annualCopayCapPerPerson: 750, // $750 per person per year (2026 dollars, indexed to median wage growth)

        // Sec 204(b): Zero copays for children and low-income
        zeroCopayIncomeThreshold: 2.0 // 200% of FPL for single individual
    },

    trustFund: {
        // Sec 206(b): Reserve Target Band
        reserveRatioMin: 0.05, // 5% of next year's outlays
        reserveRatioMax: 0.10, // 10% of next year's outlays

        // Sec 206: Automatic Rate Adjustments
        // Differentiated steps per statute (implied or specific provision)
        // Caps on total annual adjustment:
        // Employer: 0.60 pp
        // Individual: 0.30 pp (plus surtax impact)
        // Self-Employed: 0.90 pp
        autoAdjustment: {
            employerStep: 0.004, // 0.40 pp step (Accelerated for stability)
            individualStep: 0.002, // 0.20 pp step
            selfEmployedStep: 0.003, // 0.30 pp step (Sec 206(d)(3))
            surtaxStep: 0.001, // 0.10 pp step (Sec 206(d)(4))
            caps: {
                employer: 0.006, // 0.60 pp max annual (Sec 206(f)(1))
                individual: 0.003, // 0.30 pp max annual (Sec 206(f)(2))
                selfEmployed: 0.009 // 0.90 pp max annual (Sec 206(f)(3))
            }
        },

        // Legacy uniform step (kept for backward compatibility until refactor complete, but set to 0 to force use of new logic)
        rateAdjustmentStep: 0.00,

        // Safety caps to prevent runaway adjustments
        minRate: 0.0, // Cannot go below 0%
        maxRate: 0.20, // Safety cap at 20%

        // Sec 207(c): General Fund Transfer Limits (% of total outlays)
        generalFundLimits: [
            { years: [1, 2], maxPercent: 0.20 }, // Years 1-2: ≤20%
            { years: [3, 4], maxPercent: 0.10 }, // Years 3-4: ≤10%
            { years: [5, 6], maxPercent: 0.05 }, // Years 5-6: ≤5%
            { years: [7, 8], maxPercent: 0.02 }, // Years 7-8: ≤2%
            { years: [9, Infinity], maxPercent: 0.00 } // Year 9+: 0% (fully self-funded)
        ],

        // Sec 207(d)(3): Temporary Advance Limit (recession/emergency)
        tempAdvanceLimit: 0.005 // 0.5% of GDP
    },

    // ========================================================================
    // TITLE IV: PROVIDER PARTICIPATION, PAYMENT, AND PRESCRIPTION DRUG PRICING
    // ========================================================================

    providerPayment: {
        // Sec 402(c)(1): Minimum Payment Floors (% of Medicare rates)
        paymentFloor: 1.00, // 100% of Medicare for all services

        // Sec 402(c)(4): Transitional Rate Corridor (acute care hospitals only)
        transitionalCorridor: [
            { year: 1, multiplier: 1.20 }, // Year 1: 120% of Medicare
            { year: 2, multiplier: 1.10 }, // Year 2: 110%
            { year: 3, multiplier: 1.05 }, // Year 3: 105%
            { year: 4, multiplier: 1.00 }  // Year 4+: 100%
        ],

        // Sec 402(c)(3): Site Neutrality
        // Hospital outpatient = physician office rate (no facility fees)
        siteNeutralityEnforced: true,

        // Sec 402(d): Rural/CAH/Sole Community = Global Budget
        ruralGlobalBudget: true,

        // Sec 401(c)(2): Excess Charge Penalty
        excessChargePenaltyMultiplier: 3 // 3x the excess charge
    },

    drugPricing: {
        // Sec 405(b)(1): Statutory Ceiling Formula
        // Maximum = LOWER OF:
        //   (A) Domestic Value Benchmark, OR
        //   (B) 110% of International Reference Price

        irpMultiplier: 1.10, // 110% of IRP

        // Sec 405(d)(2): Reference Countries
        referenceCountries: ['UK', 'France', 'Germany', 'Japan', 'Canada'],

        // Sec 405(d)(3): IRP Reporting Penalty
        irpMisreportingPenalty: 10, // 10x the price difference

        // Sec 405(e): Innovation Safety Valve
        // Can exceed cap only if price < marginal cost + reasonable R&D return
        // AND drug is significant clinical advance
        // AND denial would cause shortage
        innovationSafetyValve: true
    },

    // ========================================================================
    // TITLE IV (CONTINUED): COST GOVERNANCE
    // ========================================================================

    sustainability: {
        // Sec 402(j): Annual Update Limit (The "Sustainability Brake")
        // Aggregate update factor = Nominal GDP Per Capita Growth + 0.5%
        // If spending exceeds target, automatic correction follows.

        index: 'GDP_PER_CAPITA',
        add_on: 0.005, // +0.5%

        // Sec 402(k): Hospital Global Budgets
        // Explicit authority for global/prospective budgets
        globalBudgetsAuthorized: true
    },

    smallInnovatorSupport: {
        // Sec 407(a)(2): Eligibility Criteria
        maxEmployees: 500,
        requiresNovelMechanism: true,
        requiresNoApprovedProducts: true,

        // Sec 407(b)(2): Grant Authorization
        annualGrantAuthorization: 500000000 // $500M per year
    },

    // ========================================================================
    // TITLE V: HEALTH WORKFORCE AND EDUCATION REFORM
    // ========================================================================

    workforce: {
        // Sec 501(b): Tuition Caps (2026 dollars, indexed to health education cost index)
        tuitionCaps: {
            medicalDegree: 15000, // $15,000 per year (MD/DO)
            nursingAlliedHealth: 10000 // $10,000 per year
        },

        // Sec 501(f)(1): Domestic Priority
        domesticSeatReservation: 0.90, // 90% of seats for legal residents

        // Sec 502(b): Service Obligation
        serviceYearsMin: 5,
        serviceYearsMax: 7,

        // Sec 503(b): Loan Forgiveness Amounts
        loanForgiveness: {
            physicians: 30000, // $30,000 per year
            nursesAllied: 15000, // $15,000 per year
            maxYears: 10
        }
    },

    // ========================================================================
    // TITLE VI: NON-MEMBER CARE AND SAFETY NET
    // ========================================================================

    safetyNet: {
        // Sec 603(a): Bridge Option Eligibility
        bridgeMinimumResidenceYears: 5,
        bridgeRequiresTaxCompliance: true,

        // Sec 604(d): Essential Infrastructure Capital Grants
        annualCapitalGrantAuthorization: 100000000000, // $100B per year (indexed to PPI construction)

        // Sec 604(b): Funding Structure
        sustainmentBlockGrantShare: 0.50, // 50% via formula
        strategicInnovationGrantShare: 0.50, // 50% via competitive grants

        // Sec 604(e): Regional Capacity Review Threshold
        strategicGrantReviewThreshold: 10000000 // $10M
    },

    // ========================================================================
    // TITLE VII: MEDICAL DEBT REDEMPTION
    // ========================================================================

    medicalDebt: {
        // Sec 701(b)(1): Annual Funding Range
        annualFundingMin: 5000000000, // $5B per year
        annualFundingMax: 15000000000, // $15B per year
        fundingYears: 10 // First 10 fiscal years
    },

    // ========================================================================
    // TITLE VIII: FRAUD, WASTE, AND ABUSE CONTROLS
    // ========================================================================

    fraud: {
        // Sec 803(a): Federal Felony Threshold
        felonyThreshold: 100000, // $100,000 in 12 months

        // Sec 805(a)(2): Human Review Time Limit
        humanReviewTimeLimit: 72, // 72 hours

        // Sec 805(b)(1)(B): False Flag Administrative Defense Fee
        falseFlag: {
            flatFee: 50, // $50 per claim
            percentageFee: 0.15 // OR 15% of claim value, whichever is greater
        }
    },

    // ========================================================================
    // TITLE IX: MEDICAL LIABILITY AND MALPRACTICE REFORM
    // ========================================================================

    malpractice: {
        // Sec 903(a): Noneconomic Damages Caps (2026-2030)
        noneconomicDamageCaps: {
            nonCatastrophic: 500000, // $500,000
            catastrophic: 1000000 // $1,000,000 (death or permanent major impairment)
        },

        // Sec 903(e): Exceptional Case Increase
        exceptionalCaseMaxIncrease: 0.50, // Up to 50% increase

        // Sec 903(d): Indexing Start Year
        indexingStartYear: 2031, // Adjust every 3 years after 2031

        // No limit on economic damages (Sec 903(c))
        economicDamagesUnlimited: true
    },

    // ========================================================================
    // TITLE X: TRANSITION AND INTEGRATION OF EXISTING PROGRAMS
    // ========================================================================

    phaseIn: {
        // Sec 1005: Phased Implementation Schedule
        // All dates relative to "date of enactment"

        planningPeriod: 24, // 24 months after enactment

        phases: [
            {
                phase: 1,
                yearAfterEnactment: 3,
                populations: [
                    'Medicare enrollees',
                    'Medicaid enrollees',
                    'CHIP enrollees',
                    'ACA subsidy recipients',
                    'Uninsured (identified)',
                    'Age 55+',
                    'Age <18'
                ]
            },
            {
                phase: 2,
                yearAfterEnactment: 5,
                populations: [
                    'Age 45-54',
                    'Age 18-25'
                ]
            },
            {
                phase: 3,
                yearAfterEnactment: 6,
                populations: [
                    'Age 35-44',
                    'Age 26-34'
                ]
            },
            {
                phase: 4,
                yearAfterEnactment: 7,
                populations: [
                    'All remaining legal residents'
                ]
            }
        ],

        // Sec 1002(c): VA Territorial Access
        vaFacilityAccessThreshold: 30 // 30 minutes
    },

    // ========================================================================
    // TITLE XII: WORKFORCE TRANSITION AND ECONOMIC STABILITY
    // ========================================================================

    workforceTransition: {
        // Sec 1204(a): Pension Bridge Eligibility
        pensionBridgeMinAge: 55,
        pensionBridgeMinYears: 10,
        pensionBridgeWageReplacement: 0.70, // 70% of lost wages

        // Sec 1205(f)(2): Severance Requirement
        minimumSeveranceMonths: 6 // 6 months of base salary
    }
};

// ========================================================================
// DERIVED PARAMETERS (calculated from bill text + external data)
// ========================================================================

export const DERIVED_PARAMETERS = {

    // Medicare Cost Base (from Medicare Trustee Reports)
    // These will be populated from actual data sources
    medicare: {
        // 2024 Medicare cost per beneficiary (Part A + Part B)
        // Source: 2024 Medicare Trustees Report
        costPerBeneficiary2024: null, // TO BE FILLED

        // Age-cost adjustment factors
        // Medicare population is older/sicker than general population
        // Source: CMS actuarial data
        ageCostCurve: null // TO BE FILLED
    },

    // International Reference Pricing
    // Source: OECD Health Statistics, national formularies
    irp: {
        // Average ratio of international prices to US prices
        // Varies by drug class (brand vs generic, specialty vs standard)
        brandDrugIRPRatio: null, // TO BE FILLED (estimated 0.40-0.60)
        genericDrugIRPRatio: 1.00 // Generics already at marginal cost
    },

    // Administrative Costs
    // Source: CMS administrative cost reports, international comparisons
    admin: {
        currentSystemOverhead: 0.12, // 12% of total spending (private insurance)
        medicareOverhead: 0.02, // 2% of total spending
        targetSafecareOverhead: 0.045, // 4.5% (Regulated Multi-Payer - e.g., Germany/Netherlands/Switzerland)
        transitionYears: 5 // Years to converge from 12% -> 4.5%
    },

    // Malpractice & Defensive Medicine
    // Source: CBO, medical literature
    malpractice: {
        directPremiumCost: 0.004, // 0.4% of provider spending
        defensiveMedicineCost: 0.03, // 3% of total spending
        reformReductionFactor: 0.50 // 50% reduction in defensive medicine
    }
};
